import {
    List,
    Modal,
    ModalTabs,
    OfferAuditView,
    PDFRenderer,
    PopUp,
    Show,
} from "components";

import { LocationGenerics } from "router/location";
import SupportingDocument from "./components/supporting-document";
import _ from "lodash";
import config from "config";
import { processUrlData } from "utils/url";
import { wrapClick } from "utils";
import { useFormik } from "formik";
import { useOfferAuditTrail } from "hooks/data/use-offers";
import { useOfferSupportingDocuments } from "hooks/data/use-supporting-documents";
import { useSearch } from "react-location";
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Trash2 } from "lucide-react";
import useApi from "utils/use-api";
import {
    deleteUnsignedPlacingSlip,
    fetchPlacingSlipBlobUrl,
} from "components/forms/slip-wizard/placing-slip-api";
import toast from "react-hot-toast";

const externalTabs = [
    {
        name: "Placing Slip",
        href: "PLACING_SLIP",
    },
    {
        name: "PlaceIT Offer Slip",
        href: "PLACE_IT_PLACING_SLIP",
    },
    {
        name: "Supporting Documents",
        href: "SUPPORTING_DOCUMENTS",
    },
    {
        name: "Offer History",
        href: "INTERNAL_OFFER_HISTORY",
    },
];
const internalTabs = [
    {
        name: "Placing Slip",
        href: "PLACING_SLIP",
    },
    {
        name: "Supporting Documents",
        href: "INTERNAL_SUPPORTING_DOCUMENTS",
    },
    {
        name: "Offer History",
        href: "INTERNAL_OFFER_HISTORY",
    },
];

const clearBlobUrl = (setter: Dispatch<SetStateAction<string | null>>) => {
    setter((prev) => {
        if (prev) {
            URL.revokeObjectURL(prev);
        }
        return null;
    });
};

export default function ViewOfferSlipContainer({
    open,
    setOpen,
}: {
    open: boolean;
    setOpen: (val: boolean) => void;
}) {
    const api = useApi();
    const [orderStatus, setOrderStatus] = useState("PLACING_SLIP");
    const [placingSlipUrl, setPlacingSlipUrl] = useState<string | null>(null);
    const [placingSlipLoading, setPlacingSlipLoading] = useState(false);
    const [hasPlacingSlip, setHasPlacingSlip] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const searchParams = useSearch<LocationGenerics>();
    const [id, type, place_it_id] = searchParams?.id?.split(":") || [];
    const { documents, loading } = useOfferSupportingDocuments({
        offerId: id || "",
    });
    const { offerHistory } = useOfferAuditTrail({
        input: { offerId: id || "" },
    });
    const form = useFormik({
        initialValues: {
            lang: "en",
        },
        onSubmit() {},
    });

    useEffect(() => {
        if (!open) {
            setDeleteConfirmOpen(false);
        }
    }, [open]);

    useEffect(() => {
        let active = true;

        if (!open || orderStatus !== "PLACING_SLIP" || !id) {
            clearBlobUrl(setPlacingSlipUrl);
            setHasPlacingSlip(false);
            setPlacingSlipLoading(false);
            return;
        }

        const loadPlacingSlip = async () => {
            setPlacingSlipLoading(true);

            try {
                const blobUrl = await fetchPlacingSlipBlobUrl(api, id);
                if (!active) {
                    URL.revokeObjectURL(blobUrl);
                    return;
                }
                setPlacingSlipUrl((prev) => {
                    if (prev) {
                        URL.revokeObjectURL(prev);
                    }
                    return blobUrl;
                });
                setHasPlacingSlip(true);
            } catch {
                if (active) {
                    setHasPlacingSlip(false);
                    clearBlobUrl(setPlacingSlipUrl);
                }
            } finally {
                if (active) {
                    setPlacingSlipLoading(false);
                }
            }
        };

        loadPlacingSlip();

        return () => {
            active = false;
        };
    }, [id, open, orderStatus]);

    const urls = useMemo(() => {
        return {
            PLACE_IT_PLACING_SLIP: `${config.asset.placeItUri}nic_form/${processUrlData(
                {
                    offer_id: id || "",
                    reinsurer_id: place_it_id,
                    lang: form.values.lang,
                },
            )}`,
            SUPPORTING_DOCUMENTS: `${config.asset.placeItUri}/generate-broker-supporting-docs/${place_it_id}`,
            INTERNAL_SUPPORTING_DOCUMENTS: `${config.asset.uri}generate-broker-supporting-docs/${id}`,
        };
    }, [id, place_it_id, form.values.lang]);

    const handleModalOpenChange = (val: boolean) => {
        if (!val && deleteConfirmOpen) {
            return;
        }
        setOpen(val);
    };

    const handleDeleteSlip = async () => {
        if (!id || deleting) return;

        setDeleting(true);
        try {
            await deleteUnsignedPlacingSlip(api, id);
            toast.success("Placing slip deleted successfully");
            setHasPlacingSlip(false);
            clearBlobUrl(setPlacingSlipUrl);
            setDeleteConfirmOpen(false);
        } catch {
            toast.error("Could not delete placing slip");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                setOpen={handleModalOpenChange}
                loading={loading}
                size="6xl"
                title="Placing Slip"
                description="Details of placing slip are shown below"
                hidePadding>
                <ModalTabs
                    tabs={type === "PLACEIT" ? externalTabs : internalTabs}
                    setOrderStatus={setOrderStatus}
                    orderStatus={orderStatus}
                />
                <Show
                    if={
                        orderStatus === "PLACING_SLIP" &&
                        hasPlacingSlip &&
                        !placingSlipLoading
                    }>
                    <div className="mb-3 p-3 flex justify-end">
                        <button
                            type="button"
                            disabled={deleting}
                            className="flex justify-center items-center gap-2 text-sm px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => setDeleteConfirmOpen(true)}>
                            <Trash2 className="h-4 w-4" />
                            <span>Delete Slip</span>
                        </button>
                    </div>
                </Show>

                <div className="space-y-4">
                    <Show
                        if={
                            documents.length > 0 &&
                            orderStatus === "INTERNAL_SUPPORTING_DOCUMENTS"
                        }>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                            <List
                                data={documents}
                                renderItem={(doc) => (
                                    <SupportingDocument
                                        key={doc}
                                        documentName={doc}
                                        offerId={id || ""}
                                    />
                                )}
                            />
                        </div>
                    </Show>

                    <Show if={orderStatus === "PLACING_SLIP"}>
                        <div className="bg-white rounded-2xl shadow">
                            <Show if={placingSlipLoading}>
                                <div className="flex-1 w-full min-h-[800px] flex flex-col items-center justify-center space-y-4 bg-card-500">
                                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500" />
                                    <p className="text-gray-500 font-medium">
                                        Loading placing slip...
                                    </p>
                                </div>
                            </Show>
                            <Show
                                if={!placingSlipLoading && !hasPlacingSlip}>
                                <div className="flex-1 w-full min-h-[400px] flex flex-col items-center justify-center space-y-4 bg-card-500 p-6">
                                    <p className="text-gray-500 text-center font-medium">
                                        No placing slip available for this
                                        offer.
                                    </p>
                                </div>
                            </Show>
                            <Show
                                if={
                                    !placingSlipLoading &&
                                    hasPlacingSlip &&
                                    Boolean(placingSlipUrl)
                                }>
                                <PDFRenderer url={placingSlipUrl || ""} />
                            </Show>
                        </div>
                    </Show>

                    <Show
                        if={[
                            "PLACE_IT_PLACING_SLIP",
                            "SUPPORTING_DOCUMENTS",
                            "INTERNAL_SUPPORTING_DOCUMENTS",
                        ].includes(orderStatus)}>
                        <div className="bg-white rounded-2xl shadow">
                            <PDFRenderer
                                reloadToken={loading ? "" : String(Date.now())}
                                url={_.get(urls, orderStatus)}
                            />
                        </div>
                    </Show>
                    <Show if={orderStatus === "INTERNAL_OFFER_HISTORY"}>
                        <div className=" rounded-2xl p-4">
                            <OfferAuditView history={offerHistory} />
                        </div>
                    </Show>
                </div>
            </Modal>

            <PopUp
                open={deleteConfirmOpen}
                setOpen={setDeleteConfirmOpen}
                variant="danger"
                title="Delete Placing Slip"
                description="This will permanently remove the uploaded placing slip for this offer."
                cancelText="Cancel"
                renderActions={() => (
                    <button
                        type="button"
                        disabled={deleting}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                        onClick={wrapClick(() => {
                            void handleDeleteSlip();
                        })}>
                        {deleting ? "Deleting..." : "Delete Placing Slip"}
                    </button>
                )}
            />
        </>
    );
}
